<template>
  <v-app>
    <v-container fluid class="pa-0 ir-page">
      <v-row no-gutters class="ir-page">
        <v-col cols="12" md="4" class="ir-left d-none d-md-flex justify-center">
          <h1 class="white--text ir-left__title">
            Nossa metodologia, seu sucesso!
          </h1>
        </v-col>

        <v-col cols="12" md="8" class="ir-right">
          <v-card class="ir-card" elevation="0">
            <v-img
              class="ir-logo"
              src="@/assets/images/connect/customer/connect-logo.webp"
              contain
              height="96"
            />

            <page-skeleton
              v-if="loading"
              variant="rows"
              :rows="4"
              class="py-4"
            />

            <!-- Convite invalido, expirado ou ja usado -->
            <div v-else-if="loadError" class="py-6 text-center">
              <v-icon size="48" color="#A826E1">mdi-link-variant-off</v-icon>
              <h2 class="ir-h2 mt-3">Convite indisponível</h2>
              <p class="ir-muted mt-2">{{ loadError }}</p>
              <v-btn
                depressed
                color="#A826E1"
                class="white--text text-none mt-2"
                to="/connect"
              >
                Ir para o login
              </v-btn>
            </div>

            <template v-else>
              <h2 class="ir-h2">Bem-vindo(a) à Pitch English!</h2>
              <p class="ir-muted mt-1">
                <b>{{ invite.teacherName || "Seu professor" }}</b> vinculou você
                como aluno. As informações da turma já foram definidas pelo
                professor — complete só os seus dados para acessar as aulas.
              </p>

              <div class="ir-summary">
                <div class="ir-summary__item">
                  <span>Nível</span><b>{{ invite.levelLabel || "-" }}</b>
                </div>
                <div class="ir-summary__item">
                  <span>Modalidade</span><b>{{ invite.formatLabel || "-" }}</b>
                </div>
                <div class="ir-summary__item">
                  <span>Frequência</span
                  ><b>{{ invite.frequencyLabel || "-" }}</b>
                </div>
                <div class="ir-summary__item">
                  <span>Aulas</span>
                  <b>
                    <div v-for="(slot, i) in invite.schedules" :key="i">
                      {{ weekDayLabel(slot.weekDay) }}, {{ slot.startHour }} às
                      {{ slot.endHour }}
                    </div>
                  </b>
                </div>
                <div v-if="invite.classCode" class="ir-summary__item">
                  <span>Turma</span><b>{{ invite.classCode }}</b>
                </div>
              </div>
              <p class="ir-muted ir-expiry">
                <v-icon small color="#8a84a0">mdi-clock-outline</v-icon>
                Este link expira em {{ formatDate(invite.expiresAt) }}.
              </p>

              <div class="ir-section">Seus dados</div>

              <v-form ref="form" v-model="valid" @submit.prevent="submit">
                <v-row dense>
                  <v-col cols="12" md="8">
                    <div class="ir-label">Nome completo</div>
                    <v-text-field
                      v-model="form.full_name"
                      :rules="[rules.required]"
                      v-bind="fieldProps"
                      placeholder="Nome completo"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="ir-label">Data de nascimento</div>
                    <v-text-field
                      v-model="birthDate"
                      v-mask="'##/##/####'"
                      :rules="[rules.required, rules.date]"
                      v-bind="fieldProps"
                      placeholder="Dia/Mês/Ano"
                    />
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <div class="ir-label">E-mail</div>
                    <v-text-field
                      :value="invite.email"
                      v-bind="fieldProps"
                      readonly
                      disabled
                      hint="Definido pelo professor no convite."
                      persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="ir-label">Telefone</div>
                    <div class="ir-phone">
                      <v-autocomplete
                        v-model="ddi"
                        class="ir-phone__ddi"
                        :items="countries"
                        item-value="ddi"
                        item-text="name"
                        :filter="filterCountry"
                        :rules="[rules.required]"
                        v-bind="fieldProps"
                        aria-label="Código do país"
                      >
                        <template #selection="{ item }">
                          +{{ item.ddi }}
                        </template>
                        <template #item="{ item }">
                          {{ item.flag }} {{ item.name }} (+{{ item.ddi }})
                        </template>
                      </v-autocomplete>
                      <v-text-field
                        v-model="form.phone"
                        v-mask="'(##)#####-####'"
                        :rules="[rules.required]"
                        v-bind="fieldProps"
                        placeholder="(99)12345-6789"
                      />
                    </div>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <div class="ir-label">Senha</div>
                    <v-text-field
                      v-model="form.password"
                      :rules="[rules.required, rules.password]"
                      v-bind="fieldProps"
                      :type="showPassword ? 'text' : 'password'"
                      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-closed'"
                      @click:append="showPassword = !showPassword"
                      placeholder="Senha"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="ir-label">Confirmar senha</div>
                    <v-text-field
                      v-model="passwordConfirm"
                      :rules="[rules.required, rules.samePassword]"
                      v-bind="fieldProps"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="Confirmar senha"
                    />
                  </v-col>
                </v-row>

                <!-- Menor de idade: mesmo bloco do cadastro publico -->
                <template v-if="form.underAge">
                  <div class="ir-section mt-2">Dados do responsável legal</div>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <div class="ir-label">Nome</div>
                      <v-text-field
                        v-model="guardian.name"
                        :rules="[rules.required]"
                        v-bind="fieldProps"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="ir-label">E-mail</div>
                      <v-text-field
                        v-model="guardian.email"
                        :rules="[rules.required, rules.email]"
                        v-bind="fieldProps"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="ir-label">Telefone</div>
                      <v-text-field
                        v-model="guardian.phone"
                        v-mask="'(##)#####-####'"
                        :rules="[rules.required]"
                        v-bind="fieldProps"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="ir-label">Data de nascimento</div>
                      <v-text-field
                        v-model="guardian.birthDate"
                        v-mask="'##/##/####'"
                        :rules="[rules.required, rules.date]"
                        v-bind="fieldProps"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="ir-label">CPF</div>
                      <v-text-field
                        v-model="guardian.document"
                        v-mask="'###.###.###-##'"
                        :rules="[rules.required, rules.cpf]"
                        v-bind="fieldProps"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="ir-label">Grau de parentesco</div>
                      <v-select
                        v-model="guardian.kinship"
                        :items="kinshipOptions"
                        :rules="[rules.required]"
                        v-bind="fieldProps"
                      />
                    </v-col>
                  </v-row>
                </template>

                <v-checkbox
                  v-model="accepted"
                  :rules="[(v) => !!v || 'Aceite os termos para continuar.']"
                >
                  <template #label>
                    <span>
                      Ao confirmar você concorda com os
                      <a target="_blank" href="/termos" @click.stop
                        >termos e condições de uso</a
                      >.
                    </span>
                  </template>
                </v-checkbox>

                <v-btn
                  block
                  large
                  depressed
                  color="#A826E1"
                  class="white--text text-none"
                  :loading="saving"
                  type="submit"
                >
                  Concluir cadastro e acessar as aulas
                </v-btn>
              </v-form>
            </template>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { VueMaskDirective } from "v-mask";
import { cpf } from "cpf-cnpj-validator";
import { differenceInYears, format, isBefore, isValid, parse } from "date-fns";
import { mapActions } from "vuex";
import api from "@/shared/pitchCustomerApi";
import { WEEK_DAY_ITEMS } from "@/shared/classPresentation";
import { countries } from "@/assets/data/contries";

const PASSWORD_RE =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%*#¢+§])[A-Za-z\d@$!%*#?$§-]{8,}$/;

function digitsWithDdi(ddi, phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits.length > 9 ? `${ddi}${digits}` : digits;
}

// Card 86akng14v: cadastro do aluno pelo link gerado pelo professor.
// Nivel, modalidade, frequencia, dias/horarios e e-mail vem do convite e nao
// sao perguntados de novo; ao concluir, a API cria a conta ja ativa e coloca o
// aluno na turma.
export default {
  name: "InviteRegister",
  directives: { mask: VueMaskDirective },
  data() {
    return {
      loading: true,
      loadError: "",
      saving: false,
      valid: false,
      showPassword: false,
      accepted: false,
      invite: {},
      birthDate: "",
      passwordConfirm: "",
      ddi: "55",
      countries,
      form: {
        full_name: "",
        phone: "",
        password: "",
        underAge: false,
      },
      guardian: {
        name: "",
        email: "",
        phone: "",
        birthDate: "",
        document: "",
        kinship: null,
      },
      kinshipOptions: [
        { text: "Pai/Mãe", value: 1 },
        { text: "Padrasto/Madrasta", value: 2 },
        { text: "Tio/Tia", value: 3 },
        { text: "Avô/Avó", value: 4 },
        { text: "Responsável legal", value: 5 },
        { text: "Outro", value: 6 },
      ],
      rules: {
        required: (v) => (v !== null && v !== "") || "Campo obrigatório.",
        email: (v) => /.+@.+\..+/.test(v || "") || "E-mail inválido.",
        password: (v) =>
          PASSWORD_RE.test(v || "") ||
          "A senha deve ter pelo menos 8 caracteres, com letra, número e caractere especial.",
        samePassword: (v) =>
          v === this.form.password || "As senhas devem ser iguais.",
        date: (v) => this.validDate(v) || "Data inválida.",
        cpf: (v) => cpf.isValid(v || "") || "CPF inválido.",
      },
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    fieldProps() {
      return {
        solo: true,
        flat: true,
        dense: true,
        "background-color": "#F1F3F5",
      };
    },
  },
  watch: {
    birthDate(value) {
      if (!this.validDate(value)) return;
      const date = parse(value, "dd/MM/yyyy", new Date());
      this.form.underAge = differenceInYears(new Date(), date) < 18;
    },
  },
  async created() {
    try {
      const res = await api.getStudentInvite(this.token);
      this.invite = res.data || {};
    } catch (error) {
      const raw = error?.response?.data?.message;
      this.loadError =
        (Array.isArray(raw) ? raw[0] : raw) ||
        "Não foi possível abrir este convite. Peça um novo link ao seu professor.";
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions(["setSignIn"]),
    validDate(value) {
      if (!value || value.length !== 10) return false;
      const date = parse(value, "dd/MM/yyyy", new Date());
      return isValid(date) && isBefore(date, new Date());
    },
    // Busca o pais pelo nome ou pelo codigo ("55", "+55").
    filterCountry(item, query) {
      const q = String(query || "")
        .toLowerCase()
        .replace(/^\+/, "");
      return (
        item.name.toLowerCase().includes(q) || String(item.ddi).startsWith(q)
      );
    },
    weekDayLabel(day) {
      return WEEK_DAY_ITEMS.find((item) => item.value === +day)?.text || "-";
    },
    formatDate(value) {
      if (!value) return "-";
      try {
        return format(new Date(value), "dd/MM/yyyy 'às' HH:mm");
      } catch (error) {
        return "-";
      }
    },
    async submit() {
      if (!this.$refs.form.validate()) return;

      const payload = {
        full_name: this.form.full_name.trim(),
        phone: digitsWithDdi(this.ddi, this.form.phone),
        birthDate: parse(this.birthDate, "dd/MM/yyyy", new Date()),
        underAge: this.form.underAge,
        password: this.form.password,
        accepted_eula: this.accepted,
      };

      if (this.form.underAge) {
        payload.legalGuardian = {
          ...this.guardian,
          phone: digitsWithDdi("55", this.guardian.phone),
        };
      }

      this.saving = true;
      try {
        await api.acceptStudentInvite(this.token, payload);
        await this.signIn();
      } catch (error) {
        const raw = error?.response?.data?.message;
        const message = Array.isArray(raw) ? raw[0] : raw;
        this.$root.$emit("display-alert", {
          text:
            message ||
            "Não foi possível concluir o cadastro. Tente novamente em instantes.",
          type: 1,
        });
      } finally {
        this.saving = false;
      }
    },
    // A conta ja nasce ativa: entra direto, sem passar pela tela de login.
    async signIn() {
      try {
        const res = await api.signIn({
          email: this.invite.email,
          password: this.form.password,
        });
        const name = res.data.user.name || "";
        const parts = name.trim().split(" ").filter(Boolean);
        this.setSignIn({
          initials: `${(parts[0] || "").substring(0, 1)}${(
            parts[1] || ""
          ).substring(0, 1)}`,
          user: {
            name: res.data.user.name,
            avatar: res.data.user.avatar,
            accountType: res.data.user.accountType,
            groups: res.data.user.groups,
            language: res.data.user.language,
            theme: res.data.user.theme,
            preference: res.data.user.preference,
            status: res.data.user.status,
            pendingInfo: res.data.user.pendingInfo,
            notificationsCount: res.data.notifications,
          },
          token: res.data.auth,
          refreshToken: res.data.refreshToken,
          module: "aluno",
        });
        this.$router.push({ name: "Dashboard" });
      } catch (error) {
        // Cadastro feito; so o login automatico falhou.
        this.$root.$emit("display-alert", {
          text: "Cadastro concluído! Entre com o seu e-mail e senha.",
          type: 0,
        });
        localStorage.setItem("module", "aluno");
        this.$router.push({ path: "/connect" });
      }
    },
  },
};
</script>

<style scoped>
.ir-page {
  min-height: 100vh;
}
.ir-left {
  background-image: url("../../../assets/images/connect/customer/background-min.webp");
  background-size: cover;
  background-position: center;
}
.ir-left__title {
  margin-top: 15vh;
  padding: 0 40px;
  font-size: 2.6rem;
  line-height: 1.15;
}
.ir-right {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 16px;
  background: #f5f4f8;
}
.ir-card {
  width: 100%;
  max-width: 720px;
  padding: 32px 40px 36px;
  border-radius: 16px !important;
  box-shadow: 0 4px 24px rgba(31, 17, 71, 0.06) !important;
}
.ir-logo {
  max-width: 260px;
  margin: 0 auto 24px;
}
.ir-phone {
  display: flex;
  gap: 8px;
}
.ir-phone__ddi {
  flex: 0 0 104px;
  max-width: 104px;
}
@media (max-width: 600px) {
  .ir-card {
    padding: 24px 16px 28px;
  }
}
.ir-h2 {
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--dk-ink, #1f1147);
}
.ir-muted {
  color: var(--dk-muted, #6b6480);
}
.ir-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
  background: #f7f2fc;
  border-radius: 12px;
  padding: 14px 16px;
  margin: 16px 0 6px;
}
.ir-summary__item span {
  display: block;
  font-size: 0.75rem;
  color: var(--dk-muted, #8a84a0);
}
.ir-summary__item b {
  color: var(--dk-ink-soft, #3d3653);
  font-weight: 600;
}
.ir-expiry {
  font-size: 0.85rem;
  margin-bottom: 24px;
}
.ir-label {
  font-size: 0.85rem;
  margin-bottom: 4px;
  color: var(--dk-ink-soft, #3d3653);
}
.ir-section {
  font-weight: 600;
  color: var(--dk-ink, #1f1147);
  margin-bottom: 8px;
}
</style>
